import { Icon } from '@/components/ds'
import { whatsappSoporte } from '@/lib/support'
import { cn } from '@/lib/utils'
import { APP_VERSION } from '@/lib/version'

export interface AccesoLayoutProps {
  /**
   * Ancho de la tarjeta. `normal` (440px) para login y recuperación; `ancho`
   * (620px) para el registro, que necesita dos columnas de campos.
   */
  ancho?: 'normal' | 'ancho'
  /** Contenido de la tarjeta. Va sin padding: lo pone cada bloque. */
  children: React.ReactNode
  /** Contenido suelto DEBAJO de la tarjeta, ej. "¿Ya tienes cuenta?". */
  pie?: React.ReactNode
}

/**
 * Marco compartido de las pantallas de acceso (login, registro, recuperación,
 * definir contraseña).
 *
 * Estructura: una sola columna con la tarjeta centrada. Ninguna pantalla de
 * acceso lleva discurso de producto al lado del formulario: quien llega a
 * cualquiera de las cuatro viene a completar un trámite concreto, y el panel
 * navy que estaba antes competía con él en vez de acompañarlo.
 *
 * Antes cada pantalla de acceso repetía su propio encabezado, su propio pie y
 * su propia versión: cuatro copias que ya habían empezado a divergir (una
 * mostraba los enlaces legales, otra no; una mostraba la versión, otra no).
 */
export function AccesoLayout({ ancho = 'normal', children, pie }: AccesoLayoutProps) {
  return (
    <div className="bg-canvas text-foreground flex min-h-screen text-[14px]">
      <main className="flex min-h-screen min-w-0 flex-1 flex-col items-center justify-center gap-[18px] px-4 pt-[26px] pb-[34px] lg:px-[34px] lg:pt-11 lg:pb-10">
        <MarcaAcceso />

        <div
          className={cn(
            'bg-card border-border animate-fc-in w-full overflow-hidden rounded-[14px] border shadow-[var(--shadow-card-hover)]',
            ancho === 'ancho' ? 'max-w-[620px]' : 'max-w-[440px]'
          )}
        >
          {children}
        </div>

        {pie}

        <PieLegal />
      </main>
    </div>
  )
}

/** Logotipo sobre la tarjeta: sólo el nombre, sin bajada de producto. */
function MarcaAcceso() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="bg-primary text-primary-foreground flex size-[30px] shrink-0 items-center justify-center rounded-lg">
        <Icon name="shield" size={19} />
      </div>
      <span className="text-[15px] leading-[1.2] font-semibold tracking-[-0.01em]">
        Fácil Control
      </span>
    </div>
  )
}

/**
 * Enlaces legales + versión.
 *
 * Los tres van como `<a>` plano y no como `<Link>` de wouter: se abren en una
 * pestaña nueva y wouter 3.x ignora `target`, así que navegaría en la misma
 * pestaña y se llevaría puesto el formulario a medio llenar.
 *
 * Soporte necesita ese `target` por el mismo motivo desde que apunta a WhatsApp:
 * `mailto:` abría el cliente de correo sin mover la página, pero `wa.me` es una
 * navegación de verdad.
 */
function PieLegal() {
  return (
    <div className="flex flex-col items-center gap-[5px] pt-1">
      <div className="flex flex-wrap items-center justify-center gap-2.5">
        <a
          href="/terminos"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-hover hover:text-primary-strong text-[11.5px]"
        >
          Términos y Condiciones
        </a>
        <span className="text-icon-soft text-[11.5px]">·</span>
        <a
          href="/privacidad"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-hover hover:text-primary-strong text-[11.5px]"
        >
          Política de Privacidad
        </a>
        <span className="text-icon-soft text-[11.5px]">·</span>
        <a
          href={whatsappSoporte('Hola, necesito ayuda para entrar a Fácil Control.')}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-hover hover:text-primary-strong text-[11.5px]"
        >
          Soporte
        </a>
      </div>
      <span className="text-soft text-[11px]">
        © {new Date().getFullYear()} Fácil Control ·{' '}
        <span className="fc-num text-[10.5px]">{APP_VERSION}</span>
      </span>
    </div>
  )
}

/**
 * Cuerpo de la tarjeta. Existe para que ninguna pantalla vuelva a inventar su
 * propio padding: 26px a los lados, 22px abajo.
 */
export function AccesoCuerpo({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-[18px] px-[26px] pt-[26px] pb-[22px]', className)}>
      {children}
    </div>
  )
}

/** Encabezado de la tarjeta: título + una línea que explica qué se espera. */
export function AccesoTitulo({ titulo, detalle }: { titulo: string; detalle: string }) {
  return (
    <div>
      <h1 className="mb-[5px] text-[21px] font-bold tracking-[-0.022em]">{titulo}</h1>
      <p className="text-muted-foreground text-[13px] leading-[1.5]">{detalle}</p>
    </div>
  )
}

/**
 * Franja gris al pie de la tarjeta. Es donde vive la salida hacia el otro flujo
 * ("crear cuenta", "volver al login") y, en el registro, el progreso + el envío.
 */
export function AccesoPieTarjeta({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'bg-surface-muted border-border-subtle flex flex-wrap items-center gap-[13px] border-t px-[26px] py-[14px]',
        className
      )}
    >
      {children}
    </div>
  )
}
